import type { ArtifactResponse } from '@/client/types.gen'
import { BpmnViewer } from '@/components/project/artifact-viewer/BpmnViewer'
import { ImageViewer } from '@/components/project/artifact-viewer/ImageViewer'
import { OfficeFileViewer } from '@/components/project/artifact-viewer/OfficeFileViewer'
import { PdfViewer } from '@/components/project/artifact-viewer/PdfViewer'
import { TextViewer } from '@/components/project/artifact-viewer/TextViewer'
import { TranscriptViewer } from '@/components/project/artifact-viewer/transcript/TranscriptViewer'
import { TranscriptViewerErrorBoundary } from '@/components/project/artifact-viewer/transcript/TranscriptViewerErrorBoundary'
import { UnsupportedFileViewer } from '@/components/project/artifact-viewer/UnsupportedFileViewer'
import type { PreviewCategory } from '@/types/artifacts'
import { isTranscriptMediaCategory } from '@/types/artifacts'

export interface ArtifactViewerBodyProps {
	projectId: string
	artifactId: string
	artifact: ArtifactResponse
	category: PreviewCategory
	transcriptActiveTab: 'transcript' | 'metadata'
	pdfActiveTab: 'preview' | 'metadata'
	imageActiveTab: 'preview' | 'metadata'
	textActiveTab: 'preview' | 'metadata'
	onDownload: () => Promise<void>
	downloading: boolean
	onArtifactEditUnsavedChange?: (hasUnsaved: boolean) => void
}

export function ArtifactViewerBody({
	projectId,
	artifactId,
	artifact,
	category,
	transcriptActiveTab,
	pdfActiveTab,
	imageActiveTab,
	textActiveTab,
	onDownload,
	downloading,
	onArtifactEditUnsavedChange,
}: ArtifactViewerBodyProps) {
	if (isTranscriptMediaCategory(category)) {
		return (
			<div className="flex min-h-0 flex-1 flex-col">
				<TranscriptViewerErrorBoundary>
					<TranscriptViewer
						key={artifact.id}
						artifact={artifact}
						activeTab={transcriptActiveTab}
						onUnsavedChange={onArtifactEditUnsavedChange}
					/>
				</TranscriptViewerErrorBoundary>
			</div>
		)
	}
	switch (category) {
		case 'word':
		case 'excel':
		case 'powerpoint':
		case 'visio':
			return (
				<OfficeFileViewer
					artifact={artifact}
					category={category}
					onDownload={onDownload}
					downloading={downloading}
				/>
			)
		case 'image':
			return (
				<ImageViewer
					artifact={artifact}
					projectId={projectId}
					artifactId={artifactId}
					activeTab={imageActiveTab}
					onDownload={onDownload}
					downloading={downloading}
				/>
			)
		case 'pdf':
			return (
				<div className="flex min-h-0 flex-1 flex-col">
					<PdfViewer artifact={artifact} activeTab={pdfActiveTab} />
				</div>
			)
		case 'text':
			return (
				<div className="flex min-h-0 flex-1 flex-col">
					<TextViewer
						artifact={artifact}
						activeTab={textActiveTab}
						onUnsavedChange={onArtifactEditUnsavedChange}
					/>
				</div>
			)
		case 'bpmn':
			return (
				<div className="flex min-h-0 flex-1 flex-col">
					<BpmnViewer artifact={artifact} activeTab={textActiveTab} />
				</div>
			)
		default:
			return (
				<UnsupportedFileViewer artifact={artifact} onDownload={onDownload} downloading={downloading} />
			)
	}
}
